'use client'

import type { ComponentProps } from 'react'
import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Switch as SwitchPrimitives } from 'radix-ui'

const switchVariants = cva(
	'peer inline-flex shrink-0 cursor-pointer items-center rounded-full border border-primary transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-transparent',
	{
		defaultVariants: {
			size: 'default',
		},
		variants: {
			size: {
				default: 'h-[32px] w-[56px]',
				sm: 'h-[24px] w-[42px]',
			},
		},
	},
)

const switchThumbVariants = cva(
	'pointer-events-none block rounded-full border border-primary bg-background ring-0 transition-transform data-[state=unchecked]:-translate-x-px',
	{
		defaultVariants: {
			size: 'default',
		},
		variants: {
			size: {
				// NOTE: translate-x = w/2+1px
				default: 'h-[32px] w-[32px] data-[state=checked]:translate-x-[23px]',
				sm: 'h-[24px] w-[24px] data-[state=checked]:translate-x-[19px]',
			},
		},
	},
)

const Switch = React.forwardRef<
	React.ElementRef<typeof SwitchPrimitives.Root>,
	React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> & {
		thumbProps?: ComponentProps<typeof SwitchPrimitives.Thumb>
	} & VariantProps<typeof switchVariants>
>(({ size, thumbProps, className, ...props }, ref) => (
	<SwitchPrimitives.Root
		className={switchVariants({ className, size })}
		{...props}
		ref={ref}
	>
		<SwitchPrimitives.Thumb
			{...thumbProps}
			className={switchThumbVariants({ size })}
		/>
	</SwitchPrimitives.Root>
))

Switch.displayName = SwitchPrimitives.Root.displayName

export { Switch }
